class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('CartDrawer');
    if (!this.drawer) return;

    this.body = document.getElementById('CartDrawerBody');
    this.main = document.getElementById('main');
    this.isOpen = false;
    this.initEventListeners();
    CartDrawer.instance = this;
  }

  initEventListeners() {
    // Open drawer
    document.querySelectorAll('.js-cart-drawer-toggle').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close drawer
    document.querySelectorAll('.js-cart-drawer-close').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Quantity adjustments inside drawer
    this.drawer.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-cart-drawer-qty-minus, .js-cart-drawer-qty-plus, .js-cart-drawer-remove');
      if (!btn) return;
      e.preventDefault();

      if (btn.classList.contains('js-cart-drawer-qty-minus')) {
        this.updateQuantity(btn.dataset.line, -1);
      } else if (btn.classList.contains('js-cart-drawer-qty-plus')) {
        this.updateQuantity(btn.dataset.line, 1);
      } else if (btn.classList.contains('js-cart-drawer-remove')) {
        this.changeQuantity(btn.dataset.line, 0);
      }
    });

    // Handle Add to Cart submissions via event delegation
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form || form.hasAttribute('data-cart-drawer-bound')) return;
      form.setAttribute('data-cart-drawer-bound', 'true');
      e.preventDefault();
      this.addToCart(form);
    });

    // Escape to close
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    };
    document.addEventListener('keydown', this.escHandler);
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.focusedBefore = document.activeElement;

    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Inert main content for screen readers
    if (this.main) {
      this.main.setAttribute('inert', '');
      this.main.setAttribute('aria-hidden', 'true');
    }

    // Set focus trap
    this.trapFocus();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';

    if (this.main) {
      this.main.removeAttribute('inert');
      this.main.removeAttribute('aria-hidden');
    }

    // Return focus to trigger element
    if (this.focusedBefore) {
      this.focusedBefore.focus();
    }
  }

  trapFocus() {
    const focusable = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first.focus();

    this.drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  async addToCart(form) {
    const formData = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerText : '';
    const errorEl = form.querySelector('.rena-product__error');

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = 'Adding...';
    }
    if (errorEl) errorEl.style.display = 'none';

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        if (submitBtn) {
          submitBtn.innerText = 'Added!';
          setTimeout(() => { submitBtn.innerText = originalText; }, 2000);
        }
        await this.refreshAndOpen();
      } else {
        const errorMsg = data.description || data.message || 'Error adding to cart';
        if (errorEl) {
          errorEl.textContent = errorMsg;
          errorEl.style.display = 'block';
        } else {
          window.alert(errorMsg);
        }
        if (submitBtn) submitBtn.innerText = originalText;
      }
    } catch {
      if (errorEl) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
      }
      if (submitBtn) submitBtn.innerText = originalText;
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  updateQuantity(line, delta) {
    const input = this.drawer.querySelector(`.js-cart-drawer-qty-input[data-line="${line}"]`);
    if (!input) return;
    const newQty = parseInt(input.value) + delta;
    if (newQty >= 0) {
      this.changeQuantity(line, newQty);
    }
  }

  async changeQuantity(line, quantity) {
    this.drawer.style.pointerEvents = 'none';
    this.drawer.style.opacity = '0.7';

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      if (response.ok) {
        await this.refresh();
      }
    } finally {
      this.drawer.style.pointerEvents = 'auto';
      this.drawer.style.opacity = '1';
    }
  }

  async refresh() {
    try {
      const response = await fetch(window.location.pathname + '?section_id=cart-drawer');
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const newDrawerBody = html.getElementById('CartDrawerBody');
      const newDrawerFooter = html.querySelector('.cart-drawer__footer');

      if (newDrawerBody) {
        this.body.innerHTML = newDrawerBody.innerHTML;

        const existingFooter = this.drawer.querySelector('.cart-drawer__footer');
        if (existingFooter && newDrawerFooter) {
          existingFooter.innerHTML = newDrawerFooter.innerHTML;
        } else if (!existingFooter && newDrawerFooter) {
          this.drawer.querySelector('.cart-drawer__content').appendChild(newDrawerFooter);
        } else if (existingFooter && !newDrawerFooter) {
          existingFooter.remove();
        }

        this.updateHeaderCount();
      }
    } catch {
      // Silently fail - drawer still usable
    }
  }

  async refreshAndOpen() {
    await this.refresh();
    this.open();
  }

  updateHeaderCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(res => res.json())
      .then(cart => {
        const badge = document.querySelector('.rena-header__cart-badge');
        if (cart.item_count > 0) {
          if (badge) {
            badge.innerText = cart.item_count;
          } else {
            const cartIcon = document.querySelector('.rena-header__cart-count');
            if (cartIcon) {
              const newBadge = document.createElement('span');
              newBadge.className = 'rena-header__cart-badge';
              newBadge.innerText = cart.item_count;
              cartIcon.appendChild(newBadge);
            }
          }
        } else if (badge) {
          badge.remove();
        }
      });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});
